import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

/**
 * The pile is dealt between these two sizes. A fixed 15 made every game the
 * same solved position — whoever knew the 4n+1 trick (or was the hard bot,
 * which is the same thing) won every time from the same seat. Varying the pile
 * varies which seat holds the winning position, and the bot re-derives its
 * play from the count, so it adapts by itself.
 */
export const NIM_MIN_START = 14;
export const NIM_MAX_START = 17;
export const NIM_MAX_TAKE = 3;

export interface NimState extends BaseState {
	kind: "nim";
	sticks: number;
	/** How many the last player took, so the board can show it. */
	lastTake: number | null;
}

export interface NimMove {
	take: number;
}

export const nim: GameDefinition<NimState, NimMove> = {
	id: "nim",
	category: "classics",
	title: "Nim Sticks",
	emoji: "🥢",
	blurb: "Take one, two or three sticks. Take the last one and you lose!",
	howTo: [
		"A pile of 14 to 17 sticks is on the table.",
		"On your turn take one, two or three of them.",
		"Whoever is forced to take the very last stick loses.",
	],

	create(rng) {
		return {
			kind: "nim",
			sticks: NIM_MIN_START + rng.int(NIM_MAX_START - NIM_MIN_START + 1),
			lastTake: null,
			// The opener has the tempo, so it is drawn by lot rather than handed
			// to whoever happens to sit in seat 0.
			turn: rng.int(2) as PlayerIndex,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const take = (raw as { take?: unknown }).take;
		if (typeof take !== "number" || !Number.isInteger(take)) return null;
		if (take < 1 || take > NIM_MAX_TAKE) return null;
		return { take };
	},

	apply(state, player, move): MoveOutcome<NimState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		if (move.take > state.sticks) return reject("There aren't that many left.");

		const sticks = state.sticks - move.take;
		if (sticks === 0) {
			// Taking the last stick loses it for you.
			const scores: [number, number] = [0, 0];
			scores[otherPlayer(player)] = 1;
			return {
				ok: true,
				state: {
					...state,
					sticks,
					lastTake: move.take,
					scores,
					turn: null,
					result: { winner: otherPlayer(player) },
				},
			};
		}

		return {
			ok: true,
			state: { ...state, sticks, lastTake: move.take, turn: otherPlayer(player) },
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const moves: NimMove[] = [];
		for (let take = 1; take <= Math.min(NIM_MAX_TAKE, state.sticks); take++) moves.push({ take });
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};

/**
 * Nim is solved: leave the other player a pile of `4n + 1` and they cannot
 * escape. Returns `null` when the position is already lost against a perfect
 * opponent, in which case anything is as good as anything else.
 */
export function nimBestTake(sticks: number): number | null {
	for (let take = 1; take <= Math.min(NIM_MAX_TAKE, sticks); take++) {
		if ((sticks - take) % 4 === 1) return take;
	}
	return null;
}
